import { useContext, useMemo } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import {
  IssuerAddOpeningBalanceModal,
  IssuerAddOpeningBalanceModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerAddOpeningBalanceModal.tsx';
import { IssuerAddPaymentModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddPaymentModal.tsx';
import {
  IssuerAddSubscriptionModal,
  IssuerAddSubscriptionModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerAddSubscriptionModal.tsx';
import { IssuerAddTransferModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddTransferModal.tsx';
import { DropdownMenuItem, ModalProviderContext } from 'ui';
import { TransactionType } from 'api';

export type TypeTransactions =
  | 'Redemption'
  | 'Transfer'
  | 'Payment'
  | 'Opening Balance'
  | 'Subscription'
  | 'Application'
  | 'DRP';

interface UseIssuerAddActivityMenuOptionsProps {
  registerId: number;
  isPortfolio?: boolean;
  shareholderId?: number;
}

export const useIssuerAddActivityMenuOptions = ({
  registerId,
  isPortfolio = false,
  shareholderId,
}: UseIssuerAddActivityMenuOptionsProps): DropdownMenuItem<TypeTransactions>[] => {
  const { openModal } = useContext(ModalProviderContext);
  const getRegisterTypeStrings = useRegisterTypeStrings();

  return useMemo<DropdownMenuItem<TypeTransactions>[]>(() => {
    let items: DropdownMenuItem<TypeTransactions>[] = [
      {
        label: getRegisterTypeStrings('Application', { capitalize: true }) as
          | 'Subscription'
          | 'Application',
        onClick: () =>
          openModal<IssuerAddSubscriptionModalProps>(IssuerAddSubscriptionModal, {
            mode: TransactionType.Subscription,
            register_id: registerId,
            shareholderId,
          }),
      },
      {
        label: 'Redemption',
        onClick: () =>
          openModal<IssuerAddSubscriptionModalProps>(IssuerAddSubscriptionModal, {
            mode: TransactionType.Redemption,
            register_id: registerId,
            shareholderId,
          }),
      },
      {
        label: 'DRP',
        onClick: () =>
          openModal<IssuerAddSubscriptionModalProps>(IssuerAddSubscriptionModal, {
            mode: TransactionType.Drp,
            register_id: registerId,
            shareholderId,
          }),
      },
      {
        label: 'Transfer',
        onClick: () =>
          openModal(IssuerAddTransferModal, { register_id: registerId, shareholderId }),
      },
      {
        label: 'Opening Balance',
        onClick: () =>
          openModal<IssuerAddOpeningBalanceModalProps>(IssuerAddOpeningBalanceModal, {
            mode: TransactionType.OpeningBalance,
            register_id: registerId,
            shareholderId,
          }),
      },
      {
        label: 'Payment',
        onClick: () =>
          openModal(IssuerAddPaymentModal, {
            shareholderId,
          }),
      },
    ];

    if (isPortfolio) {
      items = items.filter(item => item.label !== 'Transfer' && item.label !== 'Opening Balance');
    }

    return items;
  }, [getRegisterTypeStrings, isPortfolio, openModal, registerId, shareholderId]);
};
